import { useDispatch } from "react-redux";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import HeaderUsersModule from "../components/HeaderUsersModule";
import { RiShareBoxFill } from "react-icons/ri";
import { useEffect } from "react";
import { Spinner } from "../../SharedModule/components/Spinner";
import Footer from "../../SharedModule/components/Footer";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import {
  ROLE_BUSINESS_DEVELOPMENT_LEAD,
  ROLE_PRODUCT_VIEWER,
  ROLE_PROJECT_OWNER,
  ROLE_RELATIONSHIP_STEWARD,
  ROLE_SUPERVISOR,
  ROLE_TEAM_MEMBER,
  ROUTE_USERS,
  USERS_ALL_ROLES,
} from "../../SharedModule/utils/constants";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useNavigate, useParams } from "react-router";
import { useUserProfile } from "../hooks/useUserProfile";
import { resetFiltersUsers } from "../redux/reducers/usersList.reducer";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

const getRoleRowClassName = (index: number, rolesLength: number): string => {
  if (index === 0 && rolesLength > 1) return "pt-3";
  if (index === rolesLength - 1 && rolesLength > 1) return "pb-3";
  if (index !== 0 && index !== rolesLength - 1) return "";
  return "py-3";
};

export const UserRolesPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userId } = useParams();
  if (!userId) {
    navigate(ROUTE_USERS);
  }

  const {
    userRolesList,
    rolesLoaded,
    userAdminOrSuperAdmin,
    isLoadingUserProfile,
    actualUser,
    actualUserRoles,
  } = useUserProfile(Number(userId));

  const productEditor = {
    id: "00000000-0000-0000-0000-000000000000",
    name: "Product Editor",
    module: {
      id: 0,
      name: "AutomaticRoles",
    },
  };
  const productAdmin = {
    id: "00000000-0000-0000-0000-000000000000",
    name: "Product Admin",
    module: {
      id: 0,
      name: "AutomaticRoles",
    },
  };

  const groupByModule = (data) => {
    return data.reduce((acc, item) => {
      const moduleName = item.module.name;

      // declare all modules
      acc["Admin"] ??= [];
      acc["Billing"] ??= [];
      acc["Clients"] ??= [];
      acc["Products"] ??= [];
      acc["Time"] ??= [];
      acc["Users"] ??= [];
      acc["Assets"] ??= [];

      if (moduleName !== "AutomaticRoles") {
        acc[moduleName].push(item);
        // Automatics roles for specific modules
      } else if (item.name === ROLE_TEAM_MEMBER) {
        acc["Time"].push(item);
      } else if (item.name === ROLE_PRODUCT_VIEWER) {
        acc["Products"].push(item);
      } else if (item.name === ROLE_RELATIONSHIP_STEWARD) {
        acc["Clients"].push(item);
      } else if (item.name === ROLE_BUSINESS_DEVELOPMENT_LEAD) {
        acc["Clients"].push(item);
      } else if (item.name === ROLE_PROJECT_OWNER) {
        acc["Products"].push(productEditor);
        acc["Time"].push(item);
        acc["Clients"].push(item);
      } else if (item.name === ROLE_SUPERVISOR) {
        acc["Products"].push(productAdmin);
        acc["Users"].push(item);
        acc["Time"].push(item);
        acc["Billing"].push(item);
        acc["Clients"].push(item);
        acc["Assets"].push(item);
      }
      return acc;
    }, {});
  };

  useEffect(() => {
    document.title = "The Hub - User Roles";

    dispatch(setActiveTab(ROUTE_USERS));

    return () => {
      resetScrollY(globalThis.location.pathname);
      if (!globalThis.location.pathname.startsWith(ROUTE_USERS))
        dispatch(resetFiltersUsers());
    };
  }, []);

  const goToManageRoles = () => {
    const newWindow = window.open(
      process.env.REACT_APP_APPSMITH_ROLES_URL,
      "_blank"
    );
    if (newWindow) {
      newWindow.focus();
    }
  };

  return (
    <div className="content">
      <HeaderUsersModule
        actualUser={actualUser}
        isLoadingUserProfile={isLoadingUserProfile}
      />
      {(isLoadingUserProfile || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isLoadingUserProfile &&
        rolesLoaded &&
        !verifyModuleAccess(true, USERS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {!isLoadingUserProfile &&
        rolesLoaded &&
        verifyModuleAccess(true, USERS_ALL_ROLES, userRolesList) && (
          <>
            <div
              className="d-flex justify-content-between"
              style={{ margin: "2% 0" }}
            >
              <h3 style={{ marginTop: "0.5rem" }}>Assigned Roles</h3>
              {userAdminOrSuperAdmin && (
                <button
                  className="btn button-secondary"
                  type="button"
                  onClick={() => goToManageRoles()}
                >
                  Manage Roles <RiShareBoxFill />
                </button>
              )}
            </div>

            {!actualUserRoles && (
              <div className="py-5">
                <NoContent
                  title="User with not roles"
                  image={image}
                  style={{ margin: "0 auto" }}
                />
              </div>
            )}

            {actualUserRoles && (
              <table
                className="table grey-table-header-background time-confirmation my-1"
                style={{ width: "100%" }}
              >
                <thead className="align-middle sticky-header sticky">
                  <tr>
                    <th
                      className="border-left border-right border-top fw-500 py-3 ps-4 column-16"
                      id="module"
                    >
                      Module
                    </th>
                    <th
                      className="border-right border-top fw-500 py-3 ps-4 column-16"
                      id="role"
                    >
                      Roles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupByModule(actualUserRoles))
                    ?.sort(([a], [b]) => a.localeCompare(b))
                    .map(([moduleName, roles]: [any, any]) => {
                      return roles.length === 0 ? null : (
                        <tr key={moduleName}>
                          <td className="border-left border-right fw-500 vert-middle">
                            <div className="td-text ps-3">{moduleName}</div>
                          </td>
                          <td className="border-right fw-500">
                            {roles
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((role, index) => (
                                <div
                                  key={role.name}
                                  className={`td-text ps-3 ${getRoleRowClassName(
                                    index,
                                    roles.length
                                  )}`}
                                >
                                  {role.name}
                                </div>
                              ))}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </>
        )}
      <Footer />
    </div>
  );
};
